/**
 * Rating Component — Premium star rating display
 * Supports: display, interactive, sizes, with value/review count
 */
import React, { useState } from 'react';
import { Star, StarHalf } from 'lucide-react';

/**
 * StarIcon — Individual star component
 */
function StarIcon({ filled, half, size = 16, className = '' }) {
  if (half) {
    return (
      <div className={`relative ${className}`}>
        <Star
          size={size}
          className="text-neutral-300"
          strokeWidth={1.5}
        />
        <div className="absolute inset-0 overflow-hidden w-1/2">
          <Star
            size={size}
            className="text-gold-500 fill-gold-500"
            strokeWidth={1.5}
          />
        </div>
      </div>
    );
  }

  return (
    <Star
      size={size}
      className={`${filled ? 'text-gold-500 fill-gold-500' : 'text-neutral-300'} ${className}`}
      strokeWidth={1.5}
    />
  );
}

/**
 * Rating — Display star rating
 */
export function Rating({
  value = 0,
  max = 5,
  size = 'md',
  showValue = false,
  reviewCount,
  className = ''
}) {
  const sizes = {
    sm: 12,
    md: 16,
    lg: 20,
    xl: 24,
  };

  const textSizes = {
    sm: 'text-[11px]',
    md: 'text-[13px]',
    lg: 'text-[15px]',
    xl: 'text-[17px]',
  };

  const stars = [];
  for (let i = 0; i < max; i++) {
    const filled = i < Math.floor(value);
    const half = i === Math.floor(value) && value % 1 >= 0.5;
    stars.push(
      <StarIcon
        key={i}
        filled={filled}
        half={half}
        size={sizes[size]}
      />
    );
  }

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <div className="flex items-center gap-0.5">
        {stars}
      </div>
      {showValue && (
        <span className={`font-semibold text-neutral-800 ${textSizes[size]}`}>
          {value.toFixed(1)}
        </span>
      )}
      {reviewCount !== undefined && (
        <span className={`text-neutral-500 ${textSizes[size]}`}>
          ({reviewCount.toLocaleString()} {reviewCount === 1 ? 'review' : 'reviews'})
        </span>
      )}
    </div>
  );
}

/**
 * RatingInput — Interactive rating input
 */
export function RatingInput({
  value = 0,
  onChange,
  max = 5,
  size = 'lg',
  label,
  className = ''
}) {
  const [hoverValue, setHoverValue] = useState(0);

  const sizes = {
    sm: 20,
    md: 24,
    lg: 32,
    xl: 40,
  };

  const handleClick = (index) => {
    onChange?.(index + 1);
  };

  const handleMouseEnter = (index) => {
    setHoverValue(index + 1);
  };

  const handleMouseLeave = () => {
    setHoverValue(0);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-[13px] font-medium text-neutral-600 mb-2">
          {label}
        </label>
      )}
      <div className="flex items-center gap-1">
        {Array.from({ length: max }).map((_, index) => {
          const displayValue = hoverValue || value;
          const filled = index < displayValue;
          return (
            <button
              key={index}
              type="button"
              onClick={() => handleClick(index)}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              className="transition-transform duration-150 hover:scale-110 cursor-pointer"
            >
              <Star
                size={sizes[size]}
                className={`${filled ? 'text-gold-500 fill-gold-500' : 'text-neutral-300'} transition-colors duration-150`}
                strokeWidth={1.5}
              />
            </button>
          );
        })}
        {(hoverValue || value) > 0 && (
          <span className="ml-2 text-[14px] font-medium text-neutral-700">
            {hoverValue || value} / {max}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * RatingSummary — Rating summary with distribution
 */
export function RatingSummary({
  averageRating,
  totalReviews,
  distribution = {},
  className = ''
}) {
  const defaultDistribution = {
    5: 0, 4: 0, 3: 0, 2: 0, 1: 0,
    ...distribution
  };

  return (
    <div className={`flex gap-6 ${className}`}>
      {/* Left: Big Rating */}
      <div className="text-center flex-shrink-0">
        <div className="text-[48px] font-display font-medium text-neutral-900 leading-none">
          {averageRating.toFixed(1)}
        </div>
        <div className="mt-2">
          <Rating value={averageRating} size="md" />
        </div>
        <div className="mt-1 text-[13px] text-neutral-500">
          {totalReviews.toLocaleString()} reviews
        </div>
      </div>

      {/* Right: Distribution */}
      <div className="flex-1 space-y-2">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = defaultDistribution[star] || 0;
          const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-3">
              <span className="text-[13px] text-neutral-600 w-3">{star}</span>
              <Star size={12} className="text-gold-500 fill-gold-500" strokeWidth={1.5} />
              <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-gold-500 to-gold-400 rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-[12px] text-neutral-500 w-10 text-right">
                {count.toLocaleString()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Rating;
